import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a timestamp for display
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString()
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—"
  return new Date(value).toLocaleString()
}

/**
 * Risk level to CSS class mapping
 */
export function getRiskBadgeClass(riskLevel: string): string {
  switch (riskLevel) {
    case 'acceptable':
      return 'bg-[#ecfdf3] text-[#027a48] dark:bg-green-500/20 dark:text-green-400'
    case 'acceptable-clarify':
      return 'bg-[#fffaeb] text-[#b54708] dark:bg-yellow-500/20 dark:text-yellow-400'
    case 'not-acceptable':
      return 'bg-[#fef3f2] text-[#b42318] dark:bg-red-500/20 dark:text-red-400'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

/**
 * Overall risk score to CSS class mapping (mockup pastel tints in light)
 */
export function getOverallRiskClass(score: number): string {
  if (score >= 7) return 'bg-[#fef3f2] text-[#b42318] dark:bg-red-500/20 dark:text-red-400'
  if (score >= 4) return 'bg-[#fffaeb] text-[#b54708] dark:bg-yellow-500/20 dark:text-yellow-400'
  return 'bg-[#ecfdf3] text-[#027a48] dark:bg-green-500/20 dark:text-green-400'
}

/**
 * Format suggested wording with markdown-like styling
 */
export function formatWording(text: string): string {
  if (!text || text === 'N/A') return text

  // Convert **bold** to green text
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<span class="font-semibold text-emerald-700 dark:text-green-400">$1</span>')
  // Convert ~~strikethrough~~ to red text with line-through
  formatted = formatted.replace(/~~(.*?)~~/g, '<span class="text-red-700 line-through dark:text-red-400">$1</span>')

  return formatted
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return 'id-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
}
